/** Read-only React Query client for the eight GTCC time-series endpoints. */
import { useQuery, type UseQueryResult } from "@tanstack/react-query";

import { gtccGet, type GtccMeasured, type GtccResult } from "./gtcc-result.js";

const SERIES_BASE = "/v1/admin/gtcc/series";

export const SERIES_LATENCY_PATH = `${SERIES_BASE}/latency`;
export const SERIES_TELEMETRY_INGRESS_PATH = `${SERIES_BASE}/telemetry-ingress`;
export const SERIES_ALERT_SEVERITY_PATH = `${SERIES_BASE}/alert-severity`;
export const SERIES_ALERTS_WEEKLY_PATH = `${SERIES_BASE}/alerts-weekly`;
export const SERIES_DISK_GROWTH_PATH = `${SERIES_BASE}/disk-growth`;
export const SERIES_UPTIME_WEEKLY_PATH = `${SERIES_BASE}/uptime-weekly`;
export const SERIES_MAIL_DELIVERY_PATH = `${SERIES_BASE}/mail-delivery`;
export const SERIES_BACKUP_DURATION_PATH = `${SERIES_BASE}/backup-duration`;

export interface SeriesPointDto {
  readonly t: string;
  readonly v: number | null;
}
export interface SeriesLatencyDto extends GtccMeasured {
  readonly freshnessSeconds: number;
  readonly series: ReadonlyArray<{
    readonly key: "api" | "db";
    readonly unit: "ms";
    readonly points: ReadonlyArray<SeriesPointDto>;
  }>;
  readonly reason?: string;
}
export interface SeriesTelemetryIngressDto extends GtccMeasured {
  readonly unit: "positions_per_min" | "positions_per_day";
  readonly points: ReadonlyArray<SeriesPointDto>;
  readonly annotations: ReadonlyArray<{
    readonly from: string;
    readonly to: string;
    readonly label: string;
    readonly kind: "gateway_restart" | "deploy" | "other";
  }>;
  readonly reason?: string;
}
export interface SeriesAlertSeverityDto extends GtccMeasured {
  readonly total: number;
  readonly bySeverity: {
    readonly critical: number;
    readonly high: number;
    readonly medium: number;
    readonly low: number;
  };
  readonly acknowledged: number;
  readonly resolved: number;
  readonly reason?: string;
}
export interface SeriesAlertsWeeklyDto extends GtccMeasured {
  readonly points: ReadonlyArray<{ readonly weekStart: string; readonly count: number }>;
  readonly trendText: string;
  readonly reason?: string;
}
export interface SeriesDiskGrowthDto extends GtccMeasured {
  readonly points: ReadonlyArray<{ readonly t: string; readonly usedPercent: number }>;
  readonly forecast: ReadonlyArray<{ readonly t: string; readonly usedPercent: number }>;
  readonly daysToFull: number | null;
  readonly reason?: string;
}
export interface SeriesUptimeWeeklyDto extends GtccMeasured {
  readonly sloPercent: 99.9;
  readonly points: ReadonlyArray<{ readonly weekStart: string; readonly percent: number | null }>;
  readonly reason?: string;
}
export interface SeriesMailDeliveryDto extends GtccMeasured {
  readonly points: ReadonlyArray<{ readonly t: string; readonly deliveredPercent: number | null }>;
  readonly reason?: string;
}
export interface SeriesBackupDurationDto extends GtccMeasured {
  readonly windowLimitSeconds: number | null;
  readonly points: ReadonlyArray<{ readonly t: string; readonly seconds: number | null }>;
  readonly reason?: string;
}

const get = <T>(path: string, signal?: AbortSignal): Promise<GtccResult<T>> =>
  gtccGet<T>(path, signal);
export const fetchSeriesLatency = (signal?: AbortSignal) =>
  get<SeriesLatencyDto>(`${SERIES_LATENCY_PATH}?range=12w`, signal);
export const fetchSeriesTelemetryIngress = (signal?: AbortSignal) =>
  get<SeriesTelemetryIngressDto>(`${SERIES_TELEMETRY_INGRESS_PATH}?range=30d`, signal);
export const fetchSeriesAlertSeverity = (signal?: AbortSignal) =>
  get<SeriesAlertSeverityDto>(`${SERIES_ALERT_SEVERITY_PATH}?range=7d`, signal);
export const fetchSeriesAlertsWeekly = (signal?: AbortSignal) =>
  get<SeriesAlertsWeeklyDto>(`${SERIES_ALERTS_WEEKLY_PATH}?range=8w`, signal);
export const fetchSeriesDiskGrowth = (signal?: AbortSignal) =>
  get<SeriesDiskGrowthDto>(`${SERIES_DISK_GROWTH_PATH}?host=gt-db-primary-1&range=90d`, signal);
export const fetchSeriesUptimeWeekly = (signal?: AbortSignal) =>
  get<SeriesUptimeWeeklyDto>(`${SERIES_UPTIME_WEEKLY_PATH}?range=12w`, signal);
export const fetchSeriesMailDelivery = (signal?: AbortSignal) =>
  get<SeriesMailDeliveryDto>(`${SERIES_MAIL_DELIVERY_PATH}?range=30d`, signal);
export const fetchSeriesBackupDuration = (signal?: AbortSignal) =>
  get<SeriesBackupDurationDto>(`${SERIES_BACKUP_DURATION_PATH}?class=2&range=30d`, signal);

export const SERIES_LATENCY_KEY = ["gtcc", "cockpit", "series-latency"] as const;
export const SERIES_TELEMETRY_INGRESS_KEY = [
  "gtcc",
  "cockpit",
  "series-telemetry-ingress",
] as const;
export const SERIES_ALERT_SEVERITY_KEY = ["gtcc", "cockpit", "series-alert-severity"] as const;
export const SERIES_ALERTS_WEEKLY_KEY = ["gtcc", "cockpit", "series-alerts-weekly"] as const;
export const SERIES_DISK_GROWTH_KEY = ["gtcc", "cockpit", "series-disk-growth"] as const;
export const SERIES_UPTIME_WEEKLY_KEY = ["gtcc", "cockpit", "series-uptime-weekly"] as const;
export const SERIES_MAIL_DELIVERY_KEY = ["gtcc", "cockpit", "series-mail-delivery"] as const;
export const SERIES_BACKUP_DURATION_KEY = ["gtcc", "cockpit", "series-backup-duration"] as const;

type SeriesQuery<T> = UseQueryResult<GtccResult<T>, Error>;
export const useSeriesLatency = (): SeriesQuery<SeriesLatencyDto> =>
  useQuery({ queryKey: SERIES_LATENCY_KEY, queryFn: ({ signal }) => fetchSeriesLatency(signal) });
export const useSeriesTelemetryIngress = (): SeriesQuery<SeriesTelemetryIngressDto> =>
  useQuery({
    queryKey: SERIES_TELEMETRY_INGRESS_KEY,
    queryFn: ({ signal }) => fetchSeriesTelemetryIngress(signal),
  });
export const useSeriesAlertSeverity = (): SeriesQuery<SeriesAlertSeverityDto> =>
  useQuery({
    queryKey: SERIES_ALERT_SEVERITY_KEY,
    queryFn: ({ signal }) => fetchSeriesAlertSeverity(signal),
  });
export const useSeriesAlertsWeekly = (): SeriesQuery<SeriesAlertsWeeklyDto> =>
  useQuery({
    queryKey: SERIES_ALERTS_WEEKLY_KEY,
    queryFn: ({ signal }) => fetchSeriesAlertsWeekly(signal),
  });
export const useSeriesDiskGrowth = (): SeriesQuery<SeriesDiskGrowthDto> =>
  useQuery({
    queryKey: SERIES_DISK_GROWTH_KEY,
    queryFn: ({ signal }) => fetchSeriesDiskGrowth(signal),
  });
export const useSeriesUptimeWeekly = (): SeriesQuery<SeriesUptimeWeeklyDto> =>
  useQuery({
    queryKey: SERIES_UPTIME_WEEKLY_KEY,
    queryFn: ({ signal }) => fetchSeriesUptimeWeekly(signal),
  });
export const useSeriesMailDelivery = (): SeriesQuery<SeriesMailDeliveryDto> =>
  useQuery({
    queryKey: SERIES_MAIL_DELIVERY_KEY,
    queryFn: ({ signal }) => fetchSeriesMailDelivery(signal),
  });
export const useSeriesBackupDuration = (): SeriesQuery<SeriesBackupDurationDto> =>
  useQuery({
    queryKey: SERIES_BACKUP_DURATION_KEY,
    queryFn: ({ signal }) => fetchSeriesBackupDuration(signal),
  });
